import { useRouter } from "next/router";
import { useCallback } from "react";
import { PagesEnum, TabsEnum } from "../../../utils/enums";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import { scrollToCategoryWhenReady } from "../../../utils/help/navigation/scrollToCategorySection";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import { LoyaltyOfferType } from "../../../utils/loyaltyTypes";
import {
  PromoAction,
  PromoActionType,
  PromoRedirectPage,
} from "../../../utils/promoMaterial/promoMaterialTypes";
import { selectCustomerLoyaltyOffers } from "../../redux/selectors/loyaltyOfferSelectors";
import { updateCurrentHomeScreenTab } from "../../redux/slices/globalComponentsToggleSlice";
import {
  getMenuSubItemsForOffer,
  useApplyLoyaltyItemOfferFlow,
} from "../../redux/thunks/applyLoyaltyItemOfferFlow";
import { useMenuLookups } from "../useMenuLookups";
import { useAppDispatch, useAppSelector } from "../useReduxHooks";

/**
 * What a tapped pop-up CTA or banner does — shared so the two surfaces cannot
 * drift, and so the rules below live in one place:
 *
 * - An `APPLY_OFFER` whose offer this client does not hold does nothing. The
 *   server names offers by uuid; an id that no longer resolves means the offer
 *   stopped applying between the two calls.
 * - A `REDIRECT` to a target that no longer exists closes silently — story
 *   #4028's rule. The menu ids arrive already translated to core's namespace by
 *   the proxy, so there is nothing to resolve here beyond the route itself.
 * - Auth-gated destinations route through login first and resume after.
 */
/**
 * The destinations that are tabs of the home screen, not routes. Menu, rewards
 * and previous orders all live on that one screen, so a redirect to any of them
 * is a tab selection.
 */
const HOME_TAB_BY_PAGE: Partial<Record<PromoRedirectPage, TabsEnum>> = {
  [PromoRedirectPage.HOME]: TabsEnum.Menu,
  [PromoRedirectPage.MENU]: TabsEnum.Menu,
  [PromoRedirectPage.MENU_CATEGORY]: TabsEnum.Menu,
  [PromoRedirectPage.REWARDS]: TabsEnum.Loyalty,
  [PromoRedirectPage.PREVIOUS_ORDERS]: TabsEnum.PreviousOrders,
};

export const usePromoActionHandler = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const offers = useAppSelector(selectCustomerLoyaltyOffers);
  const applyLoyaltyItemOfferFlow = useApplyLoyaltyItemOfferFlow();
  const { subItemLookup } = useMenuLookups();
  const applyOffer = useCallback(
    (offerId?: string) => {
      const offer: LoyaltyOffer | undefined = offerId
        ? offers?.find((candidate) => candidate.id === offerId)
        : undefined;
      if (!offer) {
        return;
      }

      const plain = offer as unknown as LoyaltyOfferType;
      applyLoyaltyItemOfferFlow({
        offer: plain,
        menuSubItems: getMenuSubItemsForOffer(plain, subItemLookup),
      });
    },
    [offers, applyLoyaltyItemOfferFlow, subItemLookup]
  );

  /**
   * The route a redirect names, or undefined when there is nowhere to go — a
   * destination this build does not know, a target the action did not carry, or
   * a feature this restaurant does not have. Returning the href rather than
   * navigating keeps every case an expression, so no branch can fall through to
   * another destination.
   */
  /**
   * A route of its own, or undefined when there is nowhere to go — a
   * destination this build does not know, a target the action did not carry, or
   * a feature this restaurant does not have. Returning the href rather than
   * navigating keeps every case an expression, so no branch can fall through to
   * another destination.
   *
   * The home screen's own tabs are not here: see [HOME_TAB_BY_PAGE].
   */
  const hrefFor = useCallback(
    (action: PromoAction): string | undefined => {
      switch (action.page) {
        case PromoRedirectPage.ITEM:
          return action.itemId === undefined
            ? undefined
            : getPageLink(router, PagesEnum.Item, {
                as: String(action.itemId),
              }).as;

        // WALLET is deliberately absent: the stored-balance wallet is a
        // mobile-app feature with no web surface at all — not to be confused
        // with the `loyaltyCard` wallet *pass*, which is a different thing. A
        // wallet banner therefore does nothing here, like any destination this
        // build cannot honour.

        default:
          return undefined;
      }
    },
    [router]
  );

  /**
   * Most destinations are tabs of the home screen rather than pages, so they are
   * reached by selecting the tab — and by going home first when the customer is
   * elsewhere, since the tabs only exist there. Pushing the route alone would be
   * invisible to someone already on the home screen looking at another tab.
   */
  const goToHomeTab = useCallback(
    (tab: TabsEnum) => {
      dispatch(updateCurrentHomeScreenTab(tab));
      const home = getPageLink(router, PagesEnum.Home).as;
      if (router.asPath !== home) {
        router.push(home);
      }
    },
    [dispatch, router]
  );

  return useCallback(
    (action?: PromoAction) => {
      if (action?.type === PromoActionType.APPLY_OFFER) {
        applyOffer(action.offerId);

        return;
      }

      if (action?.type === PromoActionType.REDIRECT) {
        const tab = action.page && HOME_TAB_BY_PAGE[action.page];
        if (tab) {
          goToHomeTab(tab);

          // Then scroll, once the menu has rendered. Deliberately not a URL
          // hash: an anchor jump lands the section under the sticky header and
          // category bar, and pushing a hash onto the route the customer is
          // already on just reloads it. A category that no longer exists simply
          // leaves them on the menu.
          if (
            action.page === PromoRedirectPage.MENU_CATEGORY &&
            action.categoryId !== undefined
          ) {
            scrollToCategoryWhenReady(action.categoryId);
          }

          return;
        }

        const href = hrefFor(action);
        if (href) {
          router.push(href);
        }
      }

      // NONE, no action at all, and anything this build does not know: the
      // pop-up or banner simply closes.
    },
    [applyOffer, hrefFor, goToHomeTab, router]
  );
};
